"use client";

import { useEffect, useState } from "react";
import { CONSENT_EVENT } from "@/components/Analytics";

type ConsentState = { status: "accepted" | "rejected" | "custom"; analytics: boolean; marketing: boolean; at: string };

function readConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem("onomac-cookie-consent");
    if (!raw) return null;
    if (raw === "accepted") return { status: "accepted", analytics: true, marketing: true, at: new Date().toISOString() };
    if (raw === "rejected") return { status: "rejected", analytics: false, marketing: false, at: new Date().toISOString() };
    const p = JSON.parse(raw);
    if (p?.status === "accepted") return { status: "accepted", analytics: true, marketing: true, at: p.at ?? new Date().toISOString() };
    if (p?.status === "rejected") return { status: "rejected", analytics: false, marketing: false, at: p.at ?? new Date().toISOString() };
    if (p?.status === "custom") {
      return { status: "custom", analytics: !!p.analytics, marketing: !!p.marketing, at: p.at ?? new Date().toISOString() };
    }
    return null;
  } catch {
    return null;
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const existing = readConsent();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (!existing) setVisible(true);
      else {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
    } catch {
      setVisible(false);
    }
    const reopen = () => {
      const existing = readConsent();
      if (existing) {
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setCustomizing(false);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", reopen);
    return () => window.removeEventListener("open-cookie-settings", reopen);
  }, []);

  const notify = () => window.dispatchEvent(new Event(CONSENT_EVENT));

  const save = (s: ConsentState) => {
    try {
      localStorage.setItem("onomac-cookie-consent", JSON.stringify(s));
    } catch {}
    // Kanıt servera yazılır (best-effort — localStorage yalnızca önbellek)
    try {
      fetch("/api/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: s.status, analytics: s.analytics, marketing: s.marketing }),
      }).catch(() => {});
    } catch {}
    notify();
    setVisible(false);
    setCustomizing(false);
  };

  const acceptAll = () => save({ status: "accepted", analytics: true, marketing: true, at: new Date().toISOString() });
  const rejectAll = () => save({ status: "rejected", analytics: false, marketing: false, at: new Date().toISOString() });
  const saveCustom = () => save({ status: "custom", analytics, marketing, at: new Date().toISOString() });

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Çerez tercihleri"
      className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-md rounded-[32px] bg-White p-6 shadow-2xl outline outline-1 outline-offset-[-1px] outline-Background-dark sm:bottom-6 sm:left-1/2 sm:inset-x-auto sm:w-full sm:-translate-x-1/2"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-Background-light" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-Primary" aria-hidden="true">
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
            <path d="M8.5 8.5v.01M16 15.5v.01M12 12v.01M11 17v.01M7 14v.01" />
          </svg>
        </span>
        <h3 className="text-base font-bold text-800">Çerez Tercihleri</h3>
      </div>
      <p className="mt-3 text-xs leading-5 text-500">
        Deneyimini iyileştirmek ve kampanyaları ölçmek için çerezler kullanıyoruz.{" "}
        <a href="/cerez-politikasi" className="font-medium text-Primary hover:underline">Çerez Politikası</a> ve{" "}
        <a href="/kvkk" className="font-medium text-Primary hover:underline">KVKK</a> kapsamında verilerini koruyoruz.
        Onayın tarayıcında saklanır, dilediğinde değiştirebilirsin.
      </p>

      {customizing ? (
        <div className="mt-4 space-y-2 text-xs">
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-Background-light px-4 py-3 outline outline-1 outline-Background-dark">
            <span><strong className="text-800">Zorunlu</strong> — sepet, oturum, güvenlik. Her zaman açık.</span>
            <span className="rounded-full bg-White px-3 py-1 font-semibold text-500 outline outline-1 outline-Background-dark">Açık</span>
          </div>
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl bg-White px-4 py-3 outline outline-1 outline-Background-dark">
            <span><strong className="text-800">İstatistik</strong> — GA4, Yandex (14 ay).</span>
            <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="h-5 w-5 accent-[#e11d48]" />
          </label>
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl bg-White px-4 py-3 outline outline-1 outline-Background-dark">
            <span><strong className="text-800">Pazarlama</strong> — Meta, TikTok (3–6 ay).</span>
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="h-5 w-5 accent-[#e11d48]" />
          </label>
          <div className="flex gap-2 pt-1">
            <button onClick={rejectAll} className="min-h-[44px] flex-1 rounded-full bg-White px-4 py-2 text-xs font-medium uppercase text-Primary outline outline-1 outline-Background-dark transition hover:bg-Background-light active:scale-[0.98]">
              Reddet
            </button>
            <button onClick={saveCustom} className="min-h-[44px] flex-1 rounded-full bg-Primary px-4 py-2 text-xs font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98]">
              Seçimimi Kaydet
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex gap-2">
          <button onClick={rejectAll} className="min-h-[44px] flex-1 rounded-full bg-White px-4 py-2 text-xs font-medium uppercase text-Primary outline outline-1 outline-Background-dark transition hover:bg-Background-light active:scale-[0.98]">
            Reddet
          </button>
          <button onClick={() => setCustomizing(true)} className="min-h-[44px] flex-1 rounded-full bg-White px-4 py-2 text-xs font-medium uppercase text-Primary outline outline-1 outline-Background-dark transition hover:bg-Background-light active:scale-[0.98]">
            Özelleştir
          </button>
          <button onClick={acceptAll} className="min-h-[44px] flex-1 rounded-full bg-Primary px-4 py-2 text-xs font-medium uppercase text-White transition hover:bg-Primary-dark active:scale-[0.98]">
            Kabul Et
          </button>
        </div>
      )}
    </div>
  );
}
